import { Product } from 'components/molecules/Goods/Product';
import React from 'react';

interface Product {
  productId: number;
  productName: string;
  price: number;
  quantityAvailable: number;
  minimumOrderQuantity: number;
  bulkOrderPrice: number;
  barCode: string;
  productImage1: string;
  subCategory: string;
  status?: string;
  description?: string;
}

// Helper function to format category name for display
export const formatCategoryName = (categoryEnum: string): string => {
  return categoryEnum
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Mobile Category Sidebar Component
export const MobileCategorySidebar: React.FC<{
  allCategories: string[];
  groupedProducts: { categoryEnum: string; products: Product[] }[];
  selectedCategory: string | null;
  onCategorySelect: (categoryEnum: string | null) => void;
  onViewAll: (categoryEnum: string) => void;
}> = ({ allCategories, groupedProducts, selectedCategory, onCategorySelect, onViewAll }) => {
  return (
    <div className="w-full lg:w-64 bg-white border-r border-gray-200 lg:min-h-screen">
      <div className="py-8 px-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
        <div className="space-y-2 max-h-[calc(100vh)] overflow-y-auto">
          {allCategories.map(categoryEnum => {
            const displayName = formatCategoryName(categoryEnum);
            const isSelected = selectedCategory === categoryEnum;
            const categoryData = groupedProducts.find(group => group.categoryEnum === categoryEnum);
            const products = categoryData?.products || [];
            const hasProducts = products.length > 0;

            return (
              <div key={categoryEnum} className="border border-gray-200 rounded-lg overflow-auto">
                <button
                  className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200 ${isSelected ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  onClick={() => onCategorySelect(isSelected ? null : categoryEnum)}>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{displayName}</div>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={e => {
                      e.stopPropagation();
                      onViewAll(categoryEnum);
                    }}>
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isSelected ? 'rotate-90' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Desktop Bottom Category Filter Component
export const DesktopCategoryFilter: React.FC<{
  allCategories: string[];
  groupedProducts: { categoryEnum: string; products: Product[] }[];
  selectedCategory: string | null;
  onCategorySelect: (categoryEnum: string | null) => void;
  onViewAll: (categoryEnum: string) => void;
}> = ({ allCategories, groupedProducts, selectedCategory, onCategorySelect, onViewAll }) => {
  return (
    <div className="bg-white border-t border-gray-200 p-6">
      <div className="max-w-7xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Browse by Category</h3>

        {/* Clear filter button */}
        {selectedCategory && (
          <div className="mb-4">
            <button
              onClick={() => onCategorySelect(null)}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200">
              ← Show All Categories
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
          {allCategories.map(categoryEnum => {
            const displayName = formatCategoryName(categoryEnum);
            const categoryData = groupedProducts.find(group => group.categoryEnum === categoryEnum);
            const productCount = categoryData?.products.length || 0;
            const hasProducts = productCount > 0;
            const isSelected = selectedCategory === categoryEnum;

            return (
              <div
                key={categoryEnum}
                className={`relative p-4 border rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white'
                  }`}
                onClick={() => {
                  onCategorySelect(isSelected ? null : categoryEnum);
                }}>
                <div
                  className="text-center"
                  onClick={e => {
                    e.stopPropagation();
                    onViewAll(categoryEnum);
                  }}>
                  <div className="font-medium mb-1 text-gray-900">{displayName}</div>
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Mobile version of Browse by Category
export const MobileCategoryFilter: React.FC<{
  allCategories: string[];
  groupedProducts: { categoryEnum: string; products: Product[] }[];
  selectedCategory: string | null;
  onCategorySelect: (categoryEnum: string | null) => void;
  onViewAll: (categoryEnum: string) => void;
}> = ({ allCategories, groupedProducts, selectedCategory, onCategorySelect, onViewAll }) => {
  return (
    <div className="bg-white border-t border-gray-200 p-4 md:hidden">
      <h3 className="text-base font-semibold text-gray-900 mb-3">Browse by Category</h3>
      {selectedCategory && (
        <div className="mb-3">
          <button
            onClick={() => onCategorySelect(null)}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200">
            ← Show All Categories
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
        {allCategories.map(categoryEnum => {
          const displayName = formatCategoryName(categoryEnum);
          const isSelected = selectedCategory === categoryEnum;
          return (
            <div
              key={categoryEnum}
              className={`relative p-2 border rounded-lg cursor-pointer transition-all duration-200 text-center ${isSelected
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white'
                }`}
              onClick={() => onCategorySelect(isSelected ? null : categoryEnum)}>
              <div className="font-medium mb-1 text-gray-900 text-xs">{displayName}</div>
              {isSelected && (
                <div className="absolute top-1 right-1">
                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Enhanced Category Product Row with View All button
export const CategoryProductRow: React.FC<{
  categoryEnum: string;
  products: Product[];
  seller: any;
  onCategoryClick: (categoryEnum: string) => void;
  onViewAll: (categoryEnum: string) => void;
  showViewAll?: boolean;
}> = ({ categoryEnum, products, seller, onCategoryClick, onViewAll, showViewAll = true }) => {
  const displayName = formatCategoryName(categoryEnum);

  // Remove duplicates based on productId and sort products: in-stock items first, out-of-stock items last
  const uniqueProducts = products.filter((product, index, self) =>
    index === self.findIndex(p => p.productId === product.productId)
  );

  const sortedProducts = uniqueProducts.sort((a, b) => {
    // If both are in stock or both are out of stock, maintain original order
    if ((a.quantityAvailable > 0 && b.quantityAvailable > 0) ||
      (a.quantityAvailable === 0 && b.quantityAvailable === 0)) {
      return 0;
    }
    // If a is in stock and b is out of stock, a comes first
    if (a.quantityAvailable > 0 && b.quantityAvailable === 0) {
      return -1;
    }
    // If a is out of stock and b is in stock, b comes first
    return 1;
  });

  const displayProducts = sortedProducts.slice(0, 6); // Show max 6 products in preview
  const hasMoreProducts = sortedProducts.length > 6;

  return (
    <div className="mb-8">
      {/* Category Header with Arrow and View All */}
      <div className="mb-4 flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors duration-200 w-fit"
          onClick={() => onCategoryClick(categoryEnum)}>
          <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">{displayName}</h2>
        </div>

        {showViewAll && products.length > 0 && (
          <button
            onClick={() => onViewAll(categoryEnum)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2">
            View All
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>      {/* Horizontal Scrollable Products */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {displayProducts.map(product => (
            <div key={product.productId} className="flex-shrink-0 w-48 lg:w-56">
              <Product
                product={{
                  ...product,
                  seller,
                  status: product.status ?? '',
                  description: product.description ?? '',
                  quantityAvailable: product.quantityAvailable ?? ''
                }}
              />
            </div>
          ))}

          {/* Show "View More" card if there are more products */}
          {hasMoreProducts && (
            <div className="flex-shrink-0 w-48 lg:w-56">
              <div
                className="h-full border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200"
                onClick={() => onViewAll(categoryEnum)}>
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <p className="text-sm text-gray-600 text-center">View more products</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
